import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import 'mapbox-gl/dist/mapbox-gl.css'
import mapboxgl from 'mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax
import { LngLatLike } from 'mapbox-gl'
import { useEffectOnce } from 'usehooks-ts'

const initDefaults = {
  lat: 46.801111,
  lng: 8.226667,
  zoom: 11,
  style: 'mapbox://styles/mapbox/streets-v12',
  accessToken:
    'pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg',
  showNavigationControl: true,
}
export const MapBoxMap = ({
  init,
  flyTo,
  disableRotation,
  disableZoom,
  disableDrag,
  disableFullscreen,
  disablePanZoom,
  getMap,
  getMapOnInit,
  onFullScreenChange,
  children,
  locales,
}: {
  init?: {
    lng?: number
    lat?: number
    zoom?: number
    minZoom?: number
    maxZoom?: number
    style?: string
    accessToken?: string
    showNavigationControl?: boolean
  
  }
  flyTo?: mapboxgl.FlyToOptions | undefined
  disableRotation?: boolean
  disableZoom?: boolean
  disableDrag?: boolean
  disablePanZoom?: boolean
  disableFullscreen?: boolean
  getMap?: (map: mapboxgl.Map) => void
  getMapOnInit?: (map: mapboxgl.Map) => void
  onFullScreenChange?: (isFullscreen: boolean) => void
  children?: React.ReactNode
  locales?: { back:string,fullscreen:string }
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false)
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map>()
  const initWithDefaults = { ...initDefaults, ...init }

  const [initialHeight, setInitialHeight] = useState(0)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const headerElement = document.querySelector('[class*=FullHeader_root]')
    let headerHeight = 0
    if (headerElement) {
      // check if header is sticky
      const headerStyle = window.getComputedStyle(headerElement)
      const isSticky = headerStyle.position === 'sticky' //&& headerStyle.top === '0px'

      if (isSticky) {
        headerHeight = headerElement.clientHeight
        setHeaderHeight(headerHeight - 1)
      }
    }
    setInitialHeight(window.innerHeight - headerHeight)
    // setup resize listener
    const handleResize = () => {
      setHeight(window.innerHeight - headerHeight)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (!map.current) return // wait for map to initialize
    map.current.resize()
    if (onFullScreenChange) onFullScreenChange(isFullScreen)
    if (isFullScreen) {
      document.body.style.overflow = 'hidden'
      map.current.scrollZoom.enable()
      map.current.touchZoomRotate.enable()
      map.current.touchPitch.enable()
      map.current.dragPan.enable()
    } else {
      document.body.style.overflow = 'unset'

      if (disableRotation) {
        // disable map rotation using right click + drag
        map.current.dragRotate.disable()

        // disable map rotation using touch rotation gesture
        map.current.touchZoomRotate.disableRotation()
      }
      if (disableZoom) {
        map.current.scrollZoom.disable()
        map.current.touchZoomRotate.disable()
        map.current.touchPitch.disable()
      }
      if (disableDrag) {
        map.current.dragPan.disable()
      }
      if (disablePanZoom) {
        map.current.boxZoom.disable()
        map.current.doubleClickZoom.disable()
      }
      map.current.setZoom(initWithDefaults.zoom)
      map.current.setCenter([initWithDefaults.lng, initWithDefaults.lat])
    }
  }, [isFullScreen])

  useEffect(() => {
    if (!flyTo) return // wait for map to initialize
    map.current && map.current.flyTo(flyTo)
  }, [flyTo])
  useEffect(() => {
    if (!getMap) return // wait for map to initialize
    map.current && getMap(map.current)
  }, [getMap])

  useEffect(() => {
    if (map.current) return // initialize map only once
    mapboxgl.accessToken =
      'pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg'
    map.current = new mapboxgl.Map({
      container: mapContainer.current as HTMLElement,
      style: initWithDefaults.style,
      center: [initWithDefaults.lng, initWithDefaults.lat],
      zoom: initWithDefaults.zoom,
      minZoom: initWithDefaults.minZoom,
      maxZoom: initWithDefaults.maxZoom,
      maxBounds: [
        [-27.0, 15.0],
        [46.3, 67.2],
      ],
    })
    initWithDefaults.showNavigationControl &&
      map.current.addControl(new mapboxgl.NavigationControl())

    if (getMapOnInit) getMapOnInit(map.current)
    if (disableRotation) {
      // disable map rotation using right click + drag
      map.current.dragRotate.disable()

      // disable map rotation using touch rotation gesture
      map.current.touchZoomRotate.disableRotation()
    }
    if (disableZoom) {
      map.current.scrollZoom.disable()
      map.current.touchZoomRotate.disable()
      map.current.touchPitch.disable()
    }
    if (disableDrag) {
      map.current.dragPan.disable()
    }
    if (disablePanZoom) {
      map.current.boxZoom.disable()
      map.current.doubleClickZoom.disable()
    }
  })
  return (
    <MapRoot
      isFullScreen={isFullScreen}
      rotationDisabled={disableRotation}
      height={height}
    >
      <MapBoxMapRoot
        isFullScreen={isFullScreen}
        ref={mapContainer}
        className='map-container'
        height={height}
        headerheight={headerHeight}
      ></MapBoxMapRoot>
      <Overlay
        isFullScreen={isFullScreen}
        className='overlay'
        height={height}
        headerheight={headerHeight}
      >
        {children && children}
        {!disableFullscreen && (
          <FullScreenButton
            hasControls={initWithDefaults.showNavigationControl}
            onClick={() => setIsFullScreen(!isFullScreen)}
            isFullScreen={isFullScreen}
          >
            <FullScreenIcon />
            {isFullScreen ? locales.back : locales.fullscreen}
          </FullScreenButton>
        )}
      </Overlay>
    </MapRoot>
  )
}
const MapRoot = styled.div<{
  rotationDisabled: boolean | undefined
  isFullScreen: boolean
  height: number
}>`
  height: ${(props) => (props.isFullScreen ? `${props.height}px` : undefined)};
  position: relative;
  font-size: 12px;
  position: ${(props) => (props.isFullScreen ? 'fixed' : 'relative')};
  width: ${(props) => (props.isFullScreen ? '100vw' : undefined)};
  left: ${(props) => (props.isFullScreen ? '0' : undefined)};
  top: ${(props) => (props.isFullScreen ? '0' : undefined)};
  z-index: ${(props) => (props.isFullScreen ? '2990' : undefined)};
  margin-bottom: 40px;

  .mapboxgl-ctrl-attrib {
    display: flex;
    align-items: center;
    justify-content: center;
    button {
      margin: 0;
      margin-top: -5px !important;
    }
  }

  .mapboxgl-compact-show {
    button {
      margin-top: 5px !important;
    }
  }
  .mapboxgl-ctrl-bottom-right {
    bottom: -45px;
  }
  .mapboxgl-canvas {
    cursor: crosshair;
  }
  .mapboxgl-ctrl-compass {
    display: ${(props) => (props.rotationDisabled ? 'none' : 'unset')};
  }
`
const MapBoxMapRoot = styled.div<{
  isFullScreen: boolean
  height: number
  headerheight: number
}>`
  min-height: ${(props) => (props.isFullScreen ? '100vh' : '680px')};
  position: ${(props) => (props.isFullScreen ? 'fixed' : 'relative')};
  width: ${(props) => (props.isFullScreen ? '100vw' : undefined)};
  left: ${(props) => (props.isFullScreen ? '0' : undefined)};
  top: ${(props) => (props.isFullScreen ? '0' : undefined)};
  max-height: ${(props) => (props.isFullScreen ? '100vh' : '900px')};

  margin-bottom: 60px;
  @media screen and (max-width: 711px) {
    max-height: unset;
    min-height: unset;
    height: ${(props) =>
      props.isFullScreen ? props.height + 'px' : 'calc(100vh - 300px)'};
    top: ${(props) =>
      props.isFullScreen ? props.headerheight + 'px' : undefined};
    margin-bottom: 15px;
  }
`
const Overlay = styled.div<{
  isFullScreen: boolean
  height: number
  headerheight: number
}>`
  position: ${(props) => (props.isFullScreen ? 'fixed' : 'absolute')};
  top: 0;
  left: 0;
  width: 100%;
  height: ${(props) => (props.isFullScreen ? '100vh' : '100%')};
  pointer-events: none;
  /* background-color: rgba(255, 0, 0, 0.2); */
  @media screen and (max-width: 711px) {
    max-height: unset;
    min-height: unset;
    height: ${(props) =>
      props.isFullScreen ? props.height + 'px' : 'calc(100vh - 300px)'};
    top: ${(props) =>
      props.isFullScreen ? props.headerheight + 'px' : undefined};
    margin-bottom: 15px;
  }
`

const FullScreenButton = styled.button<{
  hasControls: boolean
  isFullScreen: boolean
}>`
  pointer-events: all;
  background-color: ${(props) =>
    props.isFullScreen ? 'var(--red-main)' : 'var(--text-color)'};

  color: ${(props) => (props.isFullScreen ? '#fff' : 'var(--site-background)')};
  padding: 10px;
  font-size: 15px;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  white-space: nowrap;
  right: ${(props) =>
    props.hasControls ? (props.isFullScreen ? '50%' : '10px') : '10px'};
  transform: ${(props) =>
    props.hasControls
      ? props.isFullScreen
        ? 'translate(50%)'
        : undefined
      : undefined};
  bottom: 10px;
  border: 0;

  z-index: 4000;
  transition: all 0.2s ease-in-out;

  &:hover {
    /* transform: scale(1.01); */
    cursor: pointer;
    background-color: var(--red-main);
  }
  svg {
    margin-right: 5px;
  }
`

const FullScreenIcon = () => (
  <svg
    role='button'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M5 19V14H7V17H10V19H5ZM5 10V5H10V7H7V10H5ZM14 19V17H17V14H19V19H14ZM17 10V7H14V5H19V10H17Z'
      fill='currentColor'
    />
  </svg>
)
